/**
 * Mehr-Standort Phase 4 — Werkstatt-Filial-Umschalter (nur Hauptsitz).
 *
 * Dropdown zum Umschalten des Werkstatt-Kontexts zwischen den Org-Standorten.
 * Setzt den globalen API-Header (`setApiOrgBranch`) und meldet die Auswahl an
 * die Seite zurück (`onChange`), damit diese die Werkstatt-Daten neu lädt.
 */
import { useEffect, useState } from "react";
import { Building2, ChevronDown } from "lucide-react";
import api, { setApiOrgBranch } from "../../lib/api";

export default function WorkshopBranchSwitcher({ user, value, onChange }) {
    const [branches, setBranches] = useState([]);
    const isHauptsitz = user?.branch_role === "hauptsitz";

    useEffect(() => {
        if (!isHauptsitz) return;
        api.get("/org/workshop-branches")
            .then((r) => setBranches(r.data?.locations || []))
            .catch(() => setBranches([]));
    }, [isHauptsitz]);

    if (!isHauptsitz || branches.length <= 1) return null;

    const totalOpen = branches.reduce((s, b) => s + (Number(b.open_count) || 0), 0);
    const selfId = branches.find((b) => b.is_self)?.dealer_id || null;

    const handle = (e) => {
        const v = e.target.value;
        // Aug-2026 (Bugfix Standort-Filter): auch der EIGENE Standort wird als
        // expliziter Header gesendet — ohne Header aggregiert das Backend für
        // den Hauptsitz sonst über ALLE Standorte.
        const branchId = v === "__all__" ? "__all__" : v;
        setApiOrgBranch(branchId);
        sessionStorage.setItem("tw_ws_branch", branchId);
        onChange?.(branchId);
    };

    // Ohne explizite Auswahl aggregiert das Backend (Hauptsitz) über alle
    // Standorte → Anzeige muss dann „Alle Standorte" sein, nicht der eigene.
    const shown = (!value || value === "self") ? "__all__" : value;

    return (
        <div className="inline-flex items-center gap-1.5 min-w-0 max-w-full" data-testid="workshop-branch-switcher">
            <Building2 className="w-4 h-4 text-[#FFCC00] shrink-0" />
            <div className="relative min-w-0">
                <select
                    value={shown}
                    onChange={handle}
                    className="appearance-none h-9 pl-3 pr-8 max-w-full rounded-sm border border-gray-600 bg-[#111827] text-white text-xs font-bold uppercase tracking-wider cursor-pointer hover:border-[#FFCC00]"
                    data-testid="workshop-branch-select" aria-label="Werkstatt-Standort wählen"
                >
                    <option value="__all__">▦ Alle Standorte ({totalOpen} offen)</option>
                    {branches.map((b) => (
                        <option key={b.dealer_id} value={b.dealer_id}>
                            {`${b.site_code} · ${b.street || b.company_name}${b.is_self ? " ★" : ""} (${b.open_count} offen)`}
                        </option>
                    ))}
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
        </div>
    );
}
